import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Vg2Shell } from "./Vg2Shell";
import {
  closeEvaSession,
  getEvaSession,
  getPaHandoff,
  postEvaMessage,
  startEvaSession,
} from "@/lib/vg2Eva";

/**
 * EVA closure page — Phase 7.
 * Bounded conversation on top of the current OwnerDecision + ActionPlan.
 * Never mutates upstream domain. All authoritative decisions
 * (materiality, closure, handoff eligibility) are on the backend.
 */
export default function Eva() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = React.useState("LOADING");
  const [view, setView] = React.useState(null);
  const [handoff, setHandoff] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [input, setInput] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const scroller = React.useRef(null);

  const boot = React.useCallback(async () => {
    setPhase("LOADING");
    setError(null);
    try {
      let s = await getEvaSession({ caseId }).catch(() => null);
      if (!s) s = await startEvaSession({ caseId });
      setView(s);
      setPhase(s.status === "CLOSED" ? "CLOSED" : "ACTIVE");
      if (s.status === "CLOSED") {
        const h = await getPaHandoff({ caseId }).catch(() => null);
        setHandoff(h);
      }
    } catch (e) {
      setError(e);
      if (e.code === "EVA_RUNTIME_UNAVAILABLE") setPhase("RUNTIME_UNAVAILABLE");
      else if (e.code === "EVA_RUNTIME_FAILURE") setPhase("RUNTIME_FAILURE");
      else if (e.code === "EVA_MISSING_CONTEXT") setPhase("MISSING_CONTEXT");
      else setPhase("ERROR");
    }
  }, [caseId]);

  React.useEffect(() => { boot(); }, [boot]);

  React.useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [view]);

  const canSend = view && view.status === "ACTIVE" &&
                  view.owner_turn_count < view.hard_cap_owner_turns &&
                  !sending && input.trim().length > 0;

  const send = React.useCallback(async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const nextTurn = view.owner_turn_count + 1;
      const updated = await postEvaMessage({
        caseId, sessionId: view.session_id,
        content: input.trim(), turnIndex: nextTurn,
      });
      setInput("");
      setView(updated);
      if (updated.status === "CLOSED") {
        const h = await getPaHandoff({ caseId }).catch(() => null);
        setHandoff(h);
        setPhase("CLOSED");
      }
    } catch (e) {
      setError(e);
    } finally {
      setSending(false);
    }
  }, [canSend, view, input, caseId]);

  const closeNow = React.useCallback(async () => {
    if (!view || view.status !== "ACTIVE") return;
    setSending(true);
    try {
      const updated = await closeEvaSession({
        caseId, sessionId: view.session_id,
      });
      setView(updated);
      const h = await getPaHandoff({ caseId }).catch(() => null);
      setHandoff(h);
      setPhase("CLOSED");
    } catch (e) {
      setError(e);
    } finally {
      setSending(false);
    }
  }, [view, caseId]);

  return (
    <Vg2Shell activeStep="eva" caseId={caseId}>
      <main className="max-w-3xl mx-auto px-4 py-8" data-testid="vg2-eva-root">
        <header className="mb-6">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-red mb-1">
            EVA · conversazione di chiusura
          </p>
          <h1 className="font-display text-2xl font-medium">
            Chiariamo gli ultimi punti prima di procedere
          </h1>
        </header>

        {phase === "LOADING" && (
          <p className="text-sm text-vg2-muted"
             data-testid="eva-loading">Sto preparando la conversazione…</p>
        )}

        {phase === "MISSING_CONTEXT" && (
          <MissingContextPanel
            onBackToAgisci={() => navigate(`/vg2/agisci/${caseId}`)} />
        )}

        {(phase === "RUNTIME_UNAVAILABLE" || phase === "RUNTIME_FAILURE") && (
          <RuntimePanel
            phase={phase}
            onRetry={boot}
            onBackToAgisci={() => navigate(`/vg2/agisci/${caseId}`)}
            onBackToDecidi={() => navigate(`/vg2/decidi/${caseId}`)} />
        )}

        {phase === "ERROR" && !["EVA_RUNTIME_UNAVAILABLE",
                                "EVA_RUNTIME_FAILURE",
                                "EVA_MISSING_CONTEXT"].includes(error?.code) && (
          <ErrorPanel
            error={error}
            onRetry={boot}
            onBackToAgisci={() => navigate(`/vg2/agisci/${caseId}`)}
            onBackToDecidi={() => navigate(`/vg2/decidi/${caseId}`)} />
        )}

        {(phase === "ACTIVE" || phase === "CLOSED") && view && (
          <>
            <ContextRibbon view={view} />
            <div ref={scroller}
                 className="border border-vg2-line rounded p-4 mb-4 max-h-[420px] overflow-y-auto bg-white"
                 data-testid="eva-messages">
              {view.messages?.map((m) => (
                <MessageBubble key={m.message_id} m={m} />
              ))}
            </div>

            {phase === "ACTIVE" ? (
              <ActiveComposer
                view={view}
                input={input}
                setInput={setInput}
                sending={sending}
                canSend={canSend}
                onSend={send}
                onCloseNow={closeNow} />
            ) : (
              <ClosurePanel view={view} handoff={handoff}
                            onBackToAgisci={() => navigate(`/vg2/agisci/${caseId}`)}
                            onBackToDecidi={() => navigate(`/vg2/decidi/${caseId}`)} />
            )}
          </>
        )}
      </main>
    </Vg2Shell>
  );
}


function ContextRibbon({ view }) {
  return (
    <div className="text-sm text-vg2-muted mb-4 border-l-2 border-vg2-line pl-3"
         data-testid="eva-context">
      <p><strong className="text-vg2-ink">Direzione scelta:</strong>{" "}
        {view.context.selected_direction}</p>
      {view.context.next_action_title && (
        <p><strong className="text-vg2-ink">Prossimo passo:</strong>{" "}
          {view.context.next_action_title}</p>
      )}
    </div>
  );
}


function MessageBubble({ m }) {
  const isOwner = m.role === "OWNER";
  return (
    <div className={`mb-3 flex ${isOwner ? "justify-end" : "justify-start"}`}
         data-testid={`eva-msg-${m.role.toLowerCase()}`}>
      <div className={`max-w-[80%] px-4 py-2 rounded whitespace-pre-wrap ${
        isOwner ? "bg-vg2-red text-white" : "bg-vg2-subtle text-vg2-ink"
      }`}>
        <p className="text-sm leading-relaxed">{m.content}</p>
      </div>
    </div>
  );
}


function ActiveComposer({ view, input, setInput, sending, canSend, onSend, onCloseNow }) {
  const cap = view.max_owner_turns;
  const used = view.owner_turn_count;
  return (
    <div>
      <div className="flex items-end gap-2 mb-2">
        <textarea
          className="flex-1 border border-vg2-line rounded p-2 text-sm resize-none min-h-[64px]"
          rows={2}
          placeholder="Scrivi qui la tua risposta…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={sending}
          data-testid="eva-input" />
        <button
          className="px-4 py-2 bg-vg2-red text-white rounded disabled:opacity-40 whitespace-nowrap"
          onClick={onSend}
          disabled={!canSend}
          data-testid="eva-send-btn">
          {sending ? "Invio…" : "Invia"}
        </button>
      </div>
      <div className="flex items-center justify-between text-xs text-vg2-muted">
        <span data-testid="eva-turn-count">
          Turno {used} di {cap}
          {view.hard_cap_owner_turns > cap && ` (max ${view.hard_cap_owner_turns})`}
        </span>
        <button onClick={onCloseNow}
                className="underline hover:text-vg2-ink"
                disabled={sending}
                data-testid="eva-close-now-btn">
          Chiudi la conversazione
        </button>
      </div>
    </div>
  );
}


function ClosurePanel({ view, handoff, onBackToAgisci, onBackToDecidi }) {
  const outcome = view.closure_outcome;
  return (
    <div className="border border-vg2-line rounded p-5 bg-vg2-subtle"
         data-testid="eva-closure">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-red mb-1">
        Conversazione conclusa
      </p>
      <p className="font-display text-lg font-medium mb-3"
         data-testid="eva-closure-outcome">
        {view.closure_outcome_label}
      </p>

      {outcome === "READY_FOR_PA_HANDOFF" && (
        <PaHandoffSummaryView handoff={handoff} />
      )}

      {outcome === "REASSESSMENT_REQUIRED" && (
        <div>
          <p className="text-sm text-vg2-ink mb-3">
            Ci sono informazioni nuove che vale la pena riportare
            prima di procedere. Torna in DECIDI con quanto emerso.
          </p>
          <button onClick={onBackToDecidi}
                  className="px-4 py-2 border border-vg2-red text-vg2-red rounded hover:bg-vg2-red hover:text-white"
                  data-testid="eva-back-to-decidi-btn">
            Torna a DECIDI
          </button>
        </div>
      )}

      {outcome === "CLOSED_WITH_OPEN_POINTS" && (
        <div>
          <p className="text-sm text-vg2-ink mb-3">
            Restano alcuni punti aperti. Puoi tornare al piano quando vuoi
            proseguire.
          </p>
          <button onClick={onBackToAgisci}
                  className="px-4 py-2 border border-vg2-red text-vg2-red rounded hover:bg-vg2-red hover:text-white"
                  data-testid="eva-back-to-agisci-btn">
            Torna al piano
          </button>
        </div>
      )}

      {outcome === "CLOSED_NO_HANDOFF" && (
        <p className="text-sm text-vg2-muted">
          Va bene. Puoi tornare al percorso in qualsiasi momento.
        </p>
      )}
    </div>
  );
}


function PaHandoffSummaryView({ handoff }) {
  if (!handoff) return null;
  return (
    <div data-testid="eva-pa-handoff">
      <p className="text-sm text-vg2-ink mb-3">
        Ho preparato una sintesi per il Property Advisor con quanto ci siamo
        detti. Quando sarà il momento potrai iniziare una telefonata già
        contestualizzata.
      </p>
      {handoff.recommended_call_focus?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-1">
            Su cosa si concentrerà la chiamata
          </p>
          <ul className="list-disc pl-5 text-sm text-vg2-ink">
            {handoff.recommended_call_focus.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      )}
      {handoff.what_owner_wants_from_pa &&
       handoff.what_owner_wants_from_pa !== "NOT_STATED" && (
        <p className="text-sm text-vg2-muted mb-3">
          <strong className="text-vg2-ink">Cosa hai chiesto:</strong>{" "}
          {handoff.what_owner_wants_from_pa}
        </p>
      )}
      <button
        className="px-4 py-2 bg-vg2-red text-white rounded opacity-50 cursor-not-allowed"
        disabled
        data-testid="eva-pa-cta">
        Parla con un Property Advisor
      </button>
      <p className="text-xs text-vg2-muted mt-2">
        Il contatto con il Property Advisor sarà disponibile a breve.
      </p>
    </div>
  );
}


function MissingContextPanel({ onBackToAgisci }) {
  return (
    <div className="border border-vg2-line rounded p-5"
         data-testid="eva-missing-context">
      <p className="font-medium mb-2">
        Prima serve un piano d'azione attivo
      </p>
      <p className="text-sm text-vg2-muted mb-4">
        La conversazione con EVA parte da un percorso già definito.
      </p>
      <button onClick={onBackToAgisci}
              className="px-4 py-2 border border-vg2-red text-vg2-red rounded hover:bg-vg2-red hover:text-white"
              data-testid="eva-back-to-agisci">
        Torna al piano
      </button>
    </div>
  );
}


function RuntimePanel({ phase, onRetry, onBackToAgisci, onBackToDecidi }) {
  const label = phase === "RUNTIME_UNAVAILABLE"
    ? "L'assistente non è disponibile in questo momento."
    : "L'assistente non è riuscito a rispondere. Riprova tra un attimo.";
  return (
    <div className="border border-vg2-line rounded p-5"
         data-testid="eva-runtime-panel">
      <p className="text-sm text-vg2-ink mb-4">{label}</p>
      <div className="flex flex-wrap gap-3">
        <button onClick={onRetry}
                className="px-4 py-2 border border-vg2-red text-vg2-red rounded hover:bg-vg2-red hover:text-white"
                data-testid="eva-runtime-retry">
          Riprova
        </button>
        {onBackToAgisci && (
          <button onClick={onBackToAgisci}
                  className="px-4 py-2 border border-vg2-line text-vg2-ink rounded hover:bg-vg2-subtle"
                  data-testid="eva-runtime-back-agisci">
            Torna al piano
          </button>
        )}
        {onBackToDecidi && (
          <button onClick={onBackToDecidi}
                  className="px-4 py-2 border border-vg2-line text-vg2-muted rounded hover:bg-vg2-subtle text-sm"
                  data-testid="eva-runtime-back-decidi">
            Torna a Decidi
          </button>
        )}
      </div>
    </div>
  );
}


function ErrorPanel({ error, onRetry, onBackToAgisci, onBackToDecidi }) {
  return (
    <div className="border border-vg2-line rounded p-5"
         data-testid="eva-error-panel">
      <p className="text-sm text-vg2-ink mb-2">
        C'è stato un problema. Puoi riprovare o tornare al piano.
      </p>
      {(error?.message || error?.code || error?.status) && (
        <details className="mb-4" data-testid="eva-error-details">
          <summary className="text-xs text-vg2-muted cursor-pointer">
            Dettagli tecnici
          </summary>
          <p className="text-xs text-vg2-muted mt-2 font-mono break-words">
            {error?.status ? `HTTP ${error.status} · ` : ""}
            {error?.code ? `${error.code} · ` : ""}
            {error?.message || "errore imprevisto"}
          </p>
        </details>
      )}
      <div className="flex flex-wrap gap-3">
        <button onClick={onRetry}
                className="px-4 py-2 border border-vg2-red text-vg2-red rounded hover:bg-vg2-red hover:text-white"
                data-testid="eva-error-retry">
          Riprova
        </button>
        {onBackToAgisci && (
          <button onClick={onBackToAgisci}
                  className="px-4 py-2 border border-vg2-line text-vg2-ink rounded hover:bg-vg2-subtle"
                  data-testid="eva-error-back-agisci">
            Torna al piano
          </button>
        )}
        {onBackToDecidi && (
          <button onClick={onBackToDecidi}
                  className="px-4 py-2 border border-vg2-line text-vg2-muted rounded hover:bg-vg2-subtle text-sm"
                  data-testid="eva-error-back-decidi">
            Torna a Decidi
          </button>
        )}
      </div>
    </div>
  );
}
